import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Divider,
  Tooltip,
  Typography,
  Avatar,
  Button,
  Chip,
} from '@mui/material';
import {
  Settings,
  Logout,
  Email,
  Keyboard,
  CalendarToday,
  Search as SearchIcon,
} from '@mui/icons-material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { useEmailStore } from '@/store/emailStore';
import { isEmbedded, postToParent } from '@/utils/embed';
import { useI18n } from '@/i18n';
import { usePreferences } from '@/contexts/PreferencesContext';
import { useAuth } from '@/hooks/useAuth';

interface AppHeaderProps {
  onShowKeyboardShortcuts?: () => void;
}

export const AppHeader = ({ onShowKeyboardShortcuts }: AppHeaderProps = {}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { resetNewMailCount, resetNewCalendarCount, accountAvatarUrl, accountDisplayName, accountEmail } = useEmailStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useI18n();
  const { preferences } = usePreferences();

  console.log('[AppHeader] Rendering with branding:', preferences.branding);

  // Determine logo source
  const logoSrc = preferences.branding?.logoDataUrl || '/logo.svg';
  console.log('[AppHeader] Logo source:', logoSrc?.substring(0, 50) + (logoSrc?.length > 50 ? '...' : ''));

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleMailbox = () => {
    resetNewMailCount();
    navigate('/');
    handleClose();
  };

  const handleCalendar = () => {
    resetNewCalendarCount();
    navigate('/calendar');
    handleClose();
  };

  const handleLogout = () => {
    if (isEmbedded()) {
      // Ask host to handle logout; fallback to local after short delay
      postToParent({ type: 'logout' });
      setTimeout(() => {
        void logout();
      }, 1000);
    } else {
      void logout();
    }
    handleClose();
  };

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K';
  const openCommandPalette = () => {
    // Dispatched event is picked up by App.tsx's listener — keeps the
    // header decoupled from the palette's open-state owner.
    window.dispatchEvent(new CustomEvent('webmail:open-command-palette'));
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: (theme) => theme.palette.background.paper,
        color: (theme) => theme.palette.text.primary,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: 3 }}>
        <Box
          component="img"
          src={logoSrc}
          alt={preferences.branding?.productName || 'MelpApp Mail'}
          onError={(e) => {
            console.error('[AppHeader] Logo failed to load, falling back to default');
            (e.target as HTMLImageElement).src = '/logo.svg';
          }}
          sx={{ height: 36, width: 'auto', mr: 2 }}
        />
        <Box sx={{ flexGrow: 1 }}>
          {preferences.branding?.productName && (
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {preferences.branding.productName}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Search and jump (${shortcutLabel})`}>
            <Button
              onClick={openCommandPalette}
              variant="outlined"
              size="small"
              startIcon={<SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                borderColor: 'divider',
                bgcolor: 'background.default',
                pl: 1.25,
                pr: 0.75,
                py: 0.5,
                minWidth: { xs: 0, sm: 220 },
                justifyContent: 'space-between',
                '&:hover': { borderColor: 'text.disabled' },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
              }}
            >
              <Box
                component="span"
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  fontWeight: 400,
                  display: { xs: 'none', sm: 'inline' },
                }}
              >
                Search mail, contacts…
              </Box>
              <Chip
                label={shortcutLabel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  ml: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip title={'Product Tour'}>
            <IconButton size="large" color="inherit" onClick={() => window.dispatchEvent(new CustomEvent('webmail-show-tour'))}>
              <HelpOutline />
            </IconButton>
          </Tooltip>
          <IconButton
            size="large"
            aria-label={t('header.accountMenuAria')}
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            sx={{ p: 0.5 }}
          >
            <Avatar
              src={accountAvatarUrl || undefined}
              alt={accountDisplayName || accountEmail || 'Account'}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}
            >
              {(accountDisplayName || accountEmail || '?').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleMailbox}>
              <Email sx={{ mr: 2 }} />
              {t('header.mailbox')}
            </MenuItem>
            <MenuItem onClick={handleCalendar}>
              <CalendarToday sx={{ mr: 2 }} />
              {t('sidebar.calendar')}
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <Settings sx={{ mr: 2 }} />
              {t('header.settings')}
            </MenuItem>
            {onShowKeyboardShortcuts && (
              <MenuItem onClick={() => { onShowKeyboardShortcuts(); handleClose(); }}>
                <Keyboard sx={{ mr: 2 }} />
                {t('header.keyboardShortcuts')}
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 2 }} />
              {t('header.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
